import { createSlice } from '@reduxjs/toolkit';

// ----------------------------------------------------------------------

const initialState = {
  isLoading: false,
  listProducts: [],
  productType: '', // consignment or preorder
  editConsignmentSKUMode: false,
  isBackFromAddProduct: false, // to track if user add new product in consignment sku order edit page

  // Current Consignment Order
  consignmentOrderID: '',

  // General Information
  retailerName: '',
  retailerID: '',
  retailerType: '',
  retailerSsrType: '',

  chosenBranches: [],
  branchList: [],

  paymentTerms: '',
  shippingType: '',

  salesQuote: '',
  termsDuration: '',

  // Warehouse
  warehouseName: '',
  warehouseID: '',
  warehouseDefault: '',

  // Shipping
  trackingCourier: '',
  trackingCourierID: '',

  // Address
  retailerDefaultAddressDetail: {},
  chosenAddressID: '',
  newAddressDetail: {},

  // Remarks
  remarks: '',
  salesRemarksForLogistic: '',

  // Files
  proforma: null,
  additionalFiles: []
};

// ----------------------------------------------------------------------

const slice = createSlice({
  name: 'consignmentOrder',
  initialState,

  reducers: {
    startLoading(state) {
      state.isLoading = true;
    },

    setListProducts(state, action) {
      state.isLoading = false;
      state.listProducts = action.payload;
    },

    setProductType(state, action) {
      state.isLoading = false;
      state.productType = action.payload;
    },

    setEditConsignmentSKUMode(state, action) {
      state.isLoading = false;
      state.editConsignmentSKUMode = action.payload;
    },

    setIsBackFromAddProduct(state, action) {
      state.isLoading = false;
      state.isBackFromAddProduct = action.payload;
    },

    setConsignmentOrderID(state, action) {
      state.isLoading = false;
      state.consignmentOrderID = action.payload;
    },

    // ------------------------------------------------------------------
    // GENERAL INFORMATION
    // ------------------------------------------------------------------

    setRetailerName(state, action) {
      state.isLoading = false;
      state.retailerName = action.payload;
    },

    setRetailerID(state, action) {
      state.isLoading = false;
      state.retailerID = action.payload;
    },

    setRetailerType(state, action) {
      state.isLoading = false;
      state.retailerType = action.payload;
    },

    setRetailerSsrType(state, action) {
      state.isLoading = false;
      state.retailerSsrType = action.payload;
    },

    setChosenBranches(state, action) {
      state.isLoading = false;
      state.chosenBranches = action.payload;
    },

    setBranchList(state, action) {
      state.isLoading = false;
      state.branchList = action.payload;
    },

    setPaymentTerms(state, action) {
      state.isLoading = false;
      state.paymentTerms = action.payload;
    },

    setShippingType(state, action) {
      state.isLoading = false;
      state.shippingType = action.payload;
    },

    setSalesQuote(state, action) {
      state.isLoading = false;
      state.salesQuote = action.payload;
    },

    setTermsDuration(state, action) {
      state.isLoading = false;
      state.termsDuration = action.payload;
    },

    // ------------------------------------------------------------------
    // WAREHOUSE
    // ------------------------------------------------------------------

    setWarehouseName(state, action) {
      state.isLoading = false;
      state.warehouseName = action.payload;
    },

    setWarehouseID(state, action) {
      state.isLoading = false;
      state.warehouseID = action.payload;
    },

    setWarehouseDefault(state, action) {
      state.isLoading = false;
      state.warehouseDefault = action.payload;
    },

    // ------------------------------------------------------------------
    // SHIPPING
    // ------------------------------------------------------------------

    setTrackingCourier(state, action) {
      state.isLoading = false;
      state.trackingCourier = action.payload;
    },

    setTrackingCourierID(state, action) {
      state.isLoading = false;
      state.trackingCourierID = action.payload;
    },

    // ------------------------------------------------------------------
    // ADDRESS
    // ------------------------------------------------------------------

    setRetailerDefaultAddressDetail(state, action) {
      state.isLoading = false;
      state.retailerDefaultAddressDetail = action.payload;
    },

    setChosenAddressID(state, action) {
      state.isLoading = false;
      state.chosenAddressID = action.payload;
    },

    setNewAddressDetail(state, action) {
      state.isLoading = false;
      state.newAddressDetail = action.payload;
    },

    // ------------------------------------------------------------------
    // REMARKS
    // ------------------------------------------------------------------

    setRemarks(state, action) {
      state.isLoading = false;
      state.remarks = action.payload;
    },

    setSalesRemarksForLogistic(state, action) {
      state.isLoading = false;
      state.salesRemarksForLogistic = action.payload;
    },

    // ------------------------------------------------------------------
    // FILES
    // ------------------------------------------------------------------

    setProforma(state, action) {
      state.isLoading = false;
      state.proforma = action.payload;
    },

    setAdditionalFiles(state, action) {
      state.isLoading = false;
      state.additionalFiles = action.payload;
    },

    // ------------------------------------------------------------------
    // RESET
    // ------------------------------------------------------------------

    resetConsignmentOrder(state) {
      state.listProducts = []
      state.productType = ''
      state.editConsignmentSKUMode = false
      state.isBackFromAddProduct = false

      state.consignmentOrderID = '';

      state.retailerName = '';
      state.retailerID = '';
      state.retailerType = '';
      state.retailerSsrType = '';

      state.chosenBranches = [];
      state.branchList = [];

      state.paymentTerms = '';
      state.shippingType = '';

      state.salesQuote = '';
      state.termsDuration = '';

      state.warehouseName = '';
      state.warehouseID = '';
      state.warehouseDefault = '';

      state.trackingCourier = '';
      state.trackingCourierID = '';

      state.retailerDefaultAddressDetail = {};
      state.chosenAddressID = '';
      state.newAddressDetail = {};

      state.remarks = '';
      state.salesRemarksForLogistic = '';

      state.proforma = null;
      state.additionalFiles = [];
    }
  }
});

// ----------------------------------------------------------------------
// Reducer
// ----------------------------------------------------------------------

export default slice.reducer;

// ----------------------------------------------------------------------
// Actions
// ----------------------------------------------------------------------

export const {
  startLoading
} = slice.actions;

// ----------------------------------------------------------------------
// Thunk Actions
// ----------------------------------------------------------------------

export function setListProducts(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setListProducts(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setProductType(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading())
    try {
      dispatch(slice.actions.setProductType(value))
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error))
    }
  }
}

export function setEditConsignmentSKUMode(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading())
    try {
      dispatch(slice.actions.setEditConsignmentSKUMode(value))
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error))
    }
  }
}

export function setIsBackFromAddProduct(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading())
    try {
      dispatch(slice.actions.setIsBackFromAddProduct(value))
    } catch (error) {
      console.error(error);
      dispatch(slice.actions.hasError(error))
    }
  }
}

export function setConsignmentOrderID(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setConsignmentOrderID(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setRetailerName(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setRetailerName(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setRetailerID(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setRetailerID(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setRetailerType(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setRetailerType(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setRetailerSsrType(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setRetailerSsrType(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setChosenBranches(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setChosenBranches(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setBranchList(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setBranchList(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setPaymentTerms(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setPaymentTerms(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setShippingType(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setShippingType(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setSalesQuote(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setSalesQuote(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setTermsDuration(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setTermsDuration(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setWarehouseName(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setWarehouseName(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setWarehouseID(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setWarehouseID(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setWarehouseDefault(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setWarehouseDefault(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setTrackingCourier(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setTrackingCourier(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setTrackingCourierID(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setTrackingCourierID(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setRetailerDefaultAddressDetail(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setRetailerDefaultAddressDetail(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setChosenAddressID(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setChosenAddressID(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setNewAddressDetail(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setNewAddressDetail(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setRemarks(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setRemarks(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setSalesRemarksForLogistic(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setSalesRemarksForLogistic(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setProforma(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setProforma(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function setAdditionalFiles(value) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.setAdditionalFiles(value));
    } catch (error) {
      console.error(error);
    }
  };
}

export function resetConsignmentOrder() {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      dispatch(slice.actions.resetConsignmentOrder());
    } catch (error) {
      console.error(error);
    }
  };
}